import {
  OrderedMap as ImmutableOrderedMap,
  Map as ImmutableMap
} from 'immutable';

/* Constants */
import {
  CUSTOM_FACET_ID_ARRAY,
  FACET_IDS,
  FACET_QUERY_PARAM_NAMES,
  FACET_QUERY_PARAM_NAMES_BY_FACET_IDS,
  FACET_QUERY_PARAM_NAMES_FOR_WEBTREKK, FILTER_QUERY_PARAM_NAMES,
} from '../../views/filter/facet/facetConstants';
import {PAGINATION_PARAM_NAMES} from '../../common/pagination/paginationConstants';
import {
  CUSTOM_PRICE_RANGE_FILTER_ID,
  DEFAULT_ALL_CATEGORY_ID,
  FILTER_DELIMITER,
  GENERIC_FACET_DELIMITER,
  GENERIC_FACET_PREFIX_DELIMITER, GENERIC_FILTER_ANCHOR_TEXT_REPLACE,
  PRICE_RANGE_FILTER_CURRENCY_SUFFIX,
  PRICE_RANGE_FILTER_MAX_PRICE_PLACEHOLDER,
  PRICE_RANGE_FILTER_MIN_PRICE_PLACEHOLDER,
  PRICE_RANGE_FILTER_NAME_DELIMITER,
  PRICE_RANGE_FILTER_VALUE_DELIMITER,
  PRICE_RANGE_FILTER_VALUE_SUFFIX,
} from './filterConstants';
import {CATEGORY_PAGE_BRAND_ID_DELIMITER} from '../category/categoryConstants';
import {PLATFORMS, ROUTE_PATHS} from '../../core/route/routeConstants';

/* Utils */
import {generateUrlFromRoutePath} from '../../core/route/routeUtils';
import {extractCategoryUrlKeywordAndId} from '../category/categoryUtils';
import {generateQueryString} from '../../common/url/urlUtils';
import {convertToInteger} from '../../common/number/numberUtils';
import {turkishCharacterConverter} from '../../common/string/stringUtils';
import {createImmutableMapFromList} from '../../common/collection/collectionUtils';
import {DELIVERY} from '../delivery/deliveryConstants';

function generateSelectedFiltersText(selectedFilters, selectionRowType) {
  if (selectionRowType === 'category') {
    return selectedFilters.map(filter => filter.name).join(', ')
  }
  const autoAppliedFalseFilters = selectedFilters.filter(filter => filter.isAutoApplied === false);
  return autoAppliedFalseFilters.map(filter => filter.name).join(', ')
}

function generateInitialSelectedFiltersByFacetMap(
  rawFacets,
  appliedFacetArray,
  categoryFacet,
  deliveryInfo = null,
  campaignFilters = null,
) {
  const facetMapEntries = [];

  facetMapEntries.push([
    categoryFacet.id,
    createImmutableMapFromList(categoryFacet.filterList),
  ]);

  let deliveryInfoFacet = generateDeliveryResponseFacetMapper(deliveryInfo);
  if (deliveryInfoFacet) {
    rawFacets.push(...deliveryInfoFacet)
  }

  let campaignFiltersFacet = generateCampaignFilterResponseFacetMapper(campaignFilters);
  if (campaignFiltersFacet) {
    rawFacets.push(campaignFiltersFacet)
  }

  rawFacets.forEach(
    rawFacet => {
      const {id: facetId} = rawFacet;
      const appliedFacet = appliedFacetArray.find(
        facet => facet.id === facetId,
      );

      facetMapEntries.push([
        facetId,
        appliedFacet ? createImmutableMapFromList(appliedFacet.filterList) : ImmutableMap(),
      ]);
    },
  );

  return ImmutableOrderedMap(facetMapEntries);
}

function generateCustomPriceRangeFilterNameFromValuePartials(minPrice, maxPrice) {
  let result = '';

  if (minPrice) {
    result += minPrice.toString();

    if (!maxPrice) {
      result += PRICE_RANGE_FILTER_CURRENCY_SUFFIX;
    }
  } else {
    result += PRICE_RANGE_FILTER_MIN_PRICE_PLACEHOLDER;
  }

  result += PRICE_RANGE_FILTER_NAME_DELIMITER;

  if (maxPrice) {
    result += maxPrice.toString() + PRICE_RANGE_FILTER_CURRENCY_SUFFIX;
  } else {
    result += PRICE_RANGE_FILTER_MAX_PRICE_PLACEHOLDER;
  }

  return result;
}

function generateCustomPriceRangeFilterName(priceRange) {
  return generateCustomPriceRangeFilterNameFromValuePartials(
    priceRange.minPrice,
    priceRange.maxPrice,
  )
}

function generateCustomPriceRangeFilterValue(priceRange) {
  let result = '';

  if (priceRange.minPrice) {
    result += priceRange.minPrice;
  } else {
    result += PRICE_RANGE_FILTER_MIN_PRICE_PLACEHOLDER;
  }

  result += PRICE_RANGE_FILTER_VALUE_DELIMITER;

  if (priceRange.maxPrice) {
    result += priceRange.maxPrice;
  } else {
    result += PRICE_RANGE_FILTER_MAX_PRICE_PLACEHOLDER;
  }

  return result;
}

function generateCustomPriceRangeFilterValueForDavinci(priceRange) {
  let result = '';

  result += priceRange.minPrice ? priceRange.minPrice : PRICE_RANGE_FILTER_MIN_PRICE_PLACEHOLDER;

  result += PRICE_RANGE_FILTER_VALUE_DELIMITER;

  result += priceRange.maxPrice ? priceRange.maxPrice : PRICE_RANGE_FILTER_MAX_PRICE_PLACEHOLDER;

  return result;
}

function generateQueryParamFromFilter(filter) {
  return filter.id === CUSTOM_PRICE_RANGE_FILTER_ID ? filter.value : filter.id;
}

function generateFacetPrefix(facetId) {
  return FACET_QUERY_PARAM_NAMES_BY_FACET_IDS[facetId] || facetId;
}

function updateGenericFilterParamsArray(
  genericFilterParamsArray,
  facetId,
  selectedFilterMap,
) {
  if (selectedFilterMap.size > 0) {
    genericFilterParamsArray.push(
      generateFacetPrefix(facetId) + GENERIC_FACET_PREFIX_DELIMITER +
      Array.from(selectedFilterMap.values()).map(
        generateQueryParamFromFilter,
      ).
        join(FILTER_DELIMITER),
    );
  }
}

function generateBrandsFilterQueryParam(params, facetId, selectedFilterMap) {
  return Array.from(selectedFilterMap.values()).map(brandFilter => brandFilter.id).
    join(CATEGORY_PAGE_BRAND_ID_DELIMITER);
}

function generateRatingFilterQueryParam(params, facetId, selectedFilterMap) {
  return Array.from(selectedFilterMap.values()).map(ratingFilter => ratingFilter.id).
    join(FILTER_DELIMITER);
}

function generateBrandsFilterQueryParamForOther(params, facetId, selectedFilterMap) {
  return Array.from(selectedFilterMap.values()).filter(brand => brand.isAutoApplied === true).length === Array.from(selectedFilterMap.values()).length ? null :
    Array.from(selectedFilterMap.values()).filter(brand => brand.isAutoApplied === false).
      map(brandFilter => brandFilter.id).
      join(CATEGORY_PAGE_BRAND_ID_DELIMITER);
}

function generateSingleFilterQueryParam(params, facetId, selectedFilterMap) {
  return Array.from(selectedFilterMap.values())[0].id;
}

const defaultGenerateFilterApiQueryParamsOptions = {
  ignoredFacets: [],
  customGeneratorDictionary: {
    [FACET_IDS.RATING]: generateRatingFilterQueryParam,
    [FACET_IDS.CATEGORY]: generateSingleFilterQueryParam,
    [FACET_IDS.BRAND]: generateBrandsFilterQueryParam,
    [FACET_IDS.WORD]: generateSingleFilterQueryParam,
  },
  customGeneratorDictionaryForOther: {
    [FACET_IDS.RATING]: generateRatingFilterQueryParam,
    [FACET_IDS.CATEGORY]: generateSingleFilterQueryParam,
    [FACET_IDS.BRAND]: generateBrandsFilterQueryParamForOther,
    [FACET_IDS.WORD]: generateSingleFilterQueryParam,
  },
};

function generateFilterApiQueryParams(
  selectedFiltersByFacetMap,
  options = {},
) {
  const params = {};

  if (selectedFiltersByFacetMap) {
    const genericFilterParamsArray = [];
    const finalOptions = {
      ...defaultGenerateFilterApiQueryParamsOptions,
      ...options,
    };
    const {
      ignoredFacets,
      customGeneratorDictionary,
    } = finalOptions;

    for (const [facetId, selectedFilterMap] of selectedFiltersByFacetMap) {
      if (ignoredFacets.indexOf(facetId) === -1) {
        if (CUSTOM_FACET_ID_ARRAY.indexOf(facetId) === -1) {
          updateGenericFilterParamsArray(
            genericFilterParamsArray,
            facetId,
            selectedFilterMap,
          );
        } else {
          const customGenerator = customGeneratorDictionary[facetId];
          if (customGenerator && selectedFilterMap.size > 0) {
            params[generateFacetPrefix(facetId)] =
              customGenerator(
                params,
                facetId,
                selectedFilterMap,
              );
          }
        }
      }
    }

    if (genericFilterParamsArray.length > 0) {
      params[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = genericFilterParamsArray.join(GENERIC_FACET_DELIMITER);
    }
  }

  return params;
}

function generateFilterApiQueryParamsForSearchPageClearFacetWithoutBrand(
  selectedFiltersByFacetMap,
  options = {},
) {
  const params = {};

  if (selectedFiltersByFacetMap) {
    const genericFilterParamsArray = [];
    const finalOptions = {
      ...defaultGenerateFilterApiQueryParamsOptions,
      ...options,
    };
    const {
      ignoredFacets,
      customGeneratorDictionaryForOther,
    } = finalOptions;

    for (const [facetId, selectedFilterMap] of selectedFiltersByFacetMap) {
      if (ignoredFacets.indexOf(facetId) === -1) {
        if (CUSTOM_FACET_ID_ARRAY.indexOf(facetId) === -1) {
          updateGenericFilterParamsArray(
            genericFilterParamsArray,
            facetId,
            selectedFilterMap,
          );
        } else {
          const customGenerator = customGeneratorDictionaryForOther[facetId];
          if (customGenerator && selectedFilterMap.size > 0) {
            params[generateFacetPrefix(facetId)] =
              customGenerator(
                params,
                facetId,
                selectedFilterMap,
              );
          }
        }
      }
    }

    if (genericFilterParamsArray.length > 0) {
      params[FACET_QUERY_PARAM_NAMES.GENERIC_FILTERS] = genericFilterParamsArray.join(GENERIC_FACET_DELIMITER);
    }
  }

  return params;
}

function generateValidQueryParamsFromFilter(filter, facet) {
  let result = generateQueryParamsFromFilter(filter, facet);
  if (facet.id === FACET_IDS.CATEGORY) {
    delete result.kategori;
  }
  return result;
}

function generateQueryParamsFromFilter(filter, facet) {
  return generateFilterApiQueryParams(
    ImmutableMap([[
      facet.id,
      ImmutableMap([[
        filter.id,
        filter,
      ]]),
    ]]),
  );
}

function getAppliedUniqueBrandFilter(appliedFacetArray) {
  let brandName = '';
  if (appliedFacetArray && appliedFacetArray.length > 0) {

    const appliedFacet = appliedFacetArray.find(
      facet => facet.id === FACET_IDS.BRAND,
    );

    if (appliedFacet
      && appliedFacet.rawFacet
      && appliedFacet.rawFacet.items
      && appliedFacet.rawFacet.items.length === 1) {
      brandName = appliedFacet.rawFacet.items[0].id;
    }
  }

  return brandName;
}

function getAppliedCampaignFilter(appliedFacetArray, uniqueCampaignsId) {
  let selectedCampaignIds = [];
  if (appliedFacetArray && appliedFacetArray.length > 0) {
    const appliedFacet = appliedFacetArray.find(
      facet => facet.id === uniqueCampaignsId,
    );
    selectedCampaignIds = appliedFacet?.rawFacet?.items.map((facet) => facet.id);
  }

  return selectedCampaignIds;
}

function getAppliedUniqueBrandIndexableFilterUrl(appliedFacetArray) {
  let filterUrl = '';
  if (appliedFacetArray && appliedFacetArray.length > 0) {

    const appliedIndexableFacet = appliedFacetArray.find(
      facet => facet.id !== FACET_IDS.BRAND && facet.isIndexable && facet.isIndexableWithBrandFilter,
    );

    if (appliedIndexableFacet
      && appliedIndexableFacet.rawFacet
      && appliedIndexableFacet.rawFacet.items
      && appliedIndexableFacet.rawFacet.items.length === 1) {
      filterUrl = `${appliedIndexableFacet.rawFacet.id}:${appliedIndexableFacet.rawFacet.items[0].id}`
    }
  }

  return filterUrl;
}

function generateFilterTitle(filter, filteredCategory, activeFacet) {
  let result = null;

  if (activeFacet.categoryId || activeFacet.isIndexable) {
    if (activeFacet.id === FACET_IDS.BRAND) {
      result = filteredCategory && filteredCategory.categoryName ?
        filter.name + ' ' + filteredCategory.categoryName :
        filter.name;
    }
  }

  return result;
}

function generateFilterAnchorTitle(filter, filteredCategory, activeFacet) {
  let result = null;

  if (activeFacet.categoryId || activeFacet.isIndexable) {
    if (activeFacet.id === FACET_IDS.BRAND) {
      result = filteredCategory && filteredCategory.categoryName ?
        filter.name + ' ' + filteredCategory.categoryName :
        filter.name;
    }
  }

  return result;
}

function generateFilterAnchorHref(filter, filteredCategory, activeFacet, brandName, filterUrl) {
  let result;

  if (filter.href) {
    const {
      categoryUrlKeyword,
      categoryId,
    } = extractCategoryUrlKeywordAndId(filteredCategory.categoryUrl);

    const categoryUrl = generateUrlFromRoutePath(
      ROUTE_PATHS[PLATFORMS.MOBILE].CATEGORY_PAGE,
      {
        categoryUrlKeyword,
        categoryId,
      },
    );

    result = filter?.href?.replace(GENERIC_FILTER_ANCHOR_TEXT_REPLACE, categoryUrl?.substring(1))
  } else if (activeFacet.categoryId || activeFacet.isIndexable) {
    const {
      categoryUrlKeyword,
      categoryId,
    } = extractCategoryUrlKeywordAndId(filteredCategory?.categoryUrl);

    if (activeFacet.id === FACET_IDS.BRAND) {
      result = generateUrlFromRoutePath(
        ROUTE_PATHS[PLATFORMS.MOBILE].BRAND_CATEGORY_PAGE,
        {
          categoryUrlKeyword,
          categoryId,
          brandIds: filter?.id,
        },
      );
      if (filterUrl) {
        result = `${result}?${filterUrl}`
      }
    } else {
      result = generateUrlFromRoutePath(
        activeFacet.isIndexableWithBrandFilter && brandName ? ROUTE_PATHS[PLATFORMS.MOBILE].BRAND_CATEGORY_PAGE : ROUTE_PATHS[PLATFORMS.MOBILE].CATEGORY_PAGE,
        activeFacet.isIndexableWithBrandFilter && brandName ?
          {
            categoryUrlKeyword: categoryUrlKeyword,
            categoryId: categoryId,
            brandIds: brandName,
          } :
          {
            categoryUrlKeyword,
            categoryId,
          },
        generateValidQueryParamsFromFilter(
          filter,
          activeFacet,
        ),
        {
          withPrefix: true,
          encode: false,
        },
      );
    }
  } else {
    result = null;
  }

  return result;
}

function generateListingFilterAnchorHref(filter, activeFacet, searchKeyword = null) {
  let result;

  if (activeFacet.isIndexable) {
    result = generateUrlFromRoutePath(
      ROUTE_PATHS[PLATFORMS.MOBILE].SEARCH_PAGE,
      null,
      generateQueryParams(
        filter,
        activeFacet,
        searchKeyword,
      ),
      {
        withPrefix: true,
        encode: false,
      },
    );
  } else {
    result = null;
  }

  return result;
}

function generateTagFilterAnchorHref(filter, activeFacet, pathname) {
  let result;

  if (activeFacet.isIndexable) {
    result = generateUrlFromRoutePath(
      pathname,
      null,
      generateQueryParams(
        filter,
        activeFacet,
      ),
      {
        withPrefix: true,
        encode: false,
      },
    );
  } else {
    result = null;
  }

  return result;
}

function generateQueryParams(filter, activeFacet, searchKeyword = null) {

  let queryParams = {};
  if (searchKeyword) {
    queryParams[FACET_QUERY_PARAM_NAMES.QUERY] = searchKeyword
  }

  const queryParamsFromFilter =
    generateQueryParamsFromFilter(
      filter,
      activeFacet,
    );

  return {...queryParams, ...queryParamsFromFilter};
}

function generateFilterApiQueryString(selectedFiltersByFacetMap) {
  return generateQueryString(
    generateFilterApiQueryParams(
      selectedFiltersByFacetMap,
    ),
    {
      encode: false,
    },
  );
}

function extractRawPriceValuesFromRawPriceFilterValue(rawPriceFilterValue) {
  const [rawMinPrice, rawMaxPrice] = rawPriceFilterValue.split(PRICE_RANGE_FILTER_VALUE_DELIMITER);

  return {
    minPrice: rawMinPrice,
    maxPrice: rawMaxPrice,
  };
}

function extractRawPriceValuesFromRawPriceFilterValueForDavinci(rawPriceFilterValue) {
  const [rawMinPrice, rawMaxPrice] = rawPriceFilterValue?.split(PRICE_RANGE_FILTER_VALUE_DELIMITER);
  const minPrice = parseFloat(rawMinPrice);
  const maxPrice = parseFloat(rawMaxPrice);
  const priceRange = {
    minPrice,
    maxPrice,
  };

  return generateCustomPriceRangeFilterValueForDavinci(priceRange);
}

function preprocessRawAppliedPriceFilter(rawFilter) {
  const {
    minPrice,
    maxPrice,
  } = extractRawPriceValuesFromRawPriceFilterValue(rawFilter.value);
  let result;

  if (minPrice.endsWith(PRICE_RANGE_FILTER_VALUE_SUFFIX) ||
    maxPrice.endsWith(PRICE_RANGE_FILTER_VALUE_SUFFIX)) {
    result = {
      ...rawFilter,
      name: generateCustomPriceRangeFilterNameFromValuePartials(
        minPrice,
        maxPrice,
      ),
      value: CUSTOM_PRICE_RANGE_FILTER_ID,
      id: rawFilter.value,
    };
  } else {
    result = rawFilter;
  }

  return result;
}

function checkForAppliedNonBrandFiltersInGetFiltersResponse(getFiltersResponse) {
  return !(
    getFiltersResponse.data &&
    getFiltersResponse.data.data &&
    getFiltersResponse.data.data.appliedFacets &&
    (
      getFiltersResponse.data.data.appliedFacets.length === 0 ||
      (
        getFiltersResponse.data.data.appliedFacets.length === 1 &&
        getFiltersResponse.data.data.appliedFacets[0].id === FACET_IDS.BRAND
      )
    )
  );
}

function clearExistingSelectedFiltersByFacetMap(selectedFiltersByFacetMap, categoryFacet) {
  return selectedFiltersByFacetMap.withMutations((mutableMap) => {
    for (const key of mutableMap.keys()) {
      if (key === FACET_IDS.CATEGORY) {
        mutableMap.set(key, createImmutableMapFromList(categoryFacet.filterList));
      } else {
        mutableMap.set(key, ImmutableMap());
      }
    }
  });
}

function generateAppliedFiltersArrayForDavinci(selectedFiltersByFacetMap, isListingPage, queryParams, categories) {
  let results = [];
  if (selectedFiltersByFacetMap) {
    Array.from(selectedFiltersByFacetMap.entries()).forEach(
      ([facetId, filterMap]) => {
        if (facetId !== FACET_IDS.CATEGORY) {
          Array.from(filterMap.values()).forEach(
            filter => {
              results.push({
                type: FACET_QUERY_PARAM_NAMES_BY_FACET_IDS[facetId] ? FACET_QUERY_PARAM_NAMES_BY_FACET_IDS[facetId] : facetId,
                value: facetId === FACET_IDS.PRICE ? extractRawPriceValuesFromRawPriceFilterValueForDavinci(filter.value) : filter.name,
                autoApplied: filter.isAutoApplied,
                id: facetId,
                name: filter?.title || ''
              });
            },
          )
        }
      },
    );
  }

  if (queryParams[PAGINATION_PARAM_NAMES.SORT_BY]) {
    results.push({
      type: PAGINATION_PARAM_NAMES.SORT_BY,
      value: queryParams[PAGINATION_PARAM_NAMES.SORT_BY],
      autoApplied: false,
    })
  }

  if (isListingPage && queryParams[FACET_QUERY_PARAM_NAMES.CATEGORY]) {
    results.push({
      type: FACET_QUERY_PARAM_NAMES.CATEGORY,
      value: queryParams[FACET_QUERY_PARAM_NAMES.CATEGORY],
      autoApplied: false,
    });
  }

  if (categories && Object.prototype.hasOwnProperty.call(categories, 'isAutoApplied') && categories.isAutoApplied && categories.categoryId !== DEFAULT_ALL_CATEGORY_ID) {
    results.push({
      type: FACET_QUERY_PARAM_NAMES.CATEGORY,
      value: categories.idHierarchy,
      autoApplied: true,
    })
  }

  return results;
}

function generateAppliedFacetsStringForWebtrekk(appliedFacetArray) {
  if (!appliedFacetArray) {
    return '';
  }

  let result = '';
  appliedFacetArray.forEach((appliedFacet, index) => {
    let seperator = ',';
    if (index === 0) {
      seperator = '';
    }

    let rawFacet = appliedFacet.rawFacet;
    let title = FACET_QUERY_PARAM_NAMES_FOR_WEBTREKK[rawFacet.title]
      ? FACET_QUERY_PARAM_NAMES_FOR_WEBTREKK[rawFacet.title]
      : rawFacet.title;

    let rawFacetItems = rawFacet.items.map(item => item.name).join('-');

    result += `${seperator}${title}:${rawFacetItems}`.replace('\'', '').replace('<', '').
      replace('>', '');
  });

  return turkishCharacterConverter(result);
}

function generateDeliveryResponseFacetMapper(deliveryInfo) {
  if (deliveryInfo) {
    let delivery = [];
    let nextDayDeliveryItems = [];
    let expressDeliveryItems = [];
    if (deliveryInfo?.nextDayDelivery?.values?.length > 0) {
      deliveryInfo?.nextDayDelivery.values?.map((value) => { //eslint-disable-line
        nextDayDeliveryItems.push({
          id: value.value,
          name: value.displayName,
          selected: false,
          mordorValue: value.value,
          customFilterId: 'nextDayDeliveryFilter',
          isAutoApplied: false,
        })
      })

      delivery.push({
        id: deliveryInfo?.nextDayDelivery?.key,
        title: deliveryInfo?.nextDayDelivery?.name,
        mordorId: deliveryInfo?.nextDayDelivery?.key,
        customFacetId: 'nextDayDeliveryFacet',
        hasMoreItem: false,
        isIndexableWithBrandFilter: false,
        enabled: Boolean((deliveryInfo?.nextDayDelivery?.hasProduct && deliveryInfo?.nextDayDelivery?.insideCutoff) || deliveryInfo?.nextDayDelivery?.selectedValues?.length),
        items: nextDayDeliveryItems,
      })
    }

    if (deliveryInfo?.expressDelivery?.values?.length > 0) {
      deliveryInfo?.expressDelivery.values?.map((value) => { //eslint-disable-line
        expressDeliveryItems.push({
          id: value.value,
          name: value.displayName,
          selected: false,
          mordorValue: value.value,
          customFilterId: 'expressDeliveryFilter',
          isAutoApplied: false,
        })
      })

      delivery.push({
        id: deliveryInfo?.expressDelivery?.key,
        title: deliveryInfo?.expressDelivery?.name,
        mordorId: deliveryInfo?.expressDelivery?.key,
        customFacetId: 'expressDeliveryFacet',
        hasMoreItem: false,
        isIndexableWithBrandFilter: false,
        enabled: Boolean((deliveryInfo?.expressDelivery?.hasProduct && deliveryInfo?.expressDelivery?.insideCutoff) || deliveryInfo?.expressDelivery?.selectedValues?.length),
        items: expressDeliveryItems,
      })
    }

    return delivery;
  }
  return null;
}

function generateCampaignFilterResponseFacetMapper(campaignFilters, originalFacetId) {
  if (campaignFilters) {
    let items = [];
    if (campaignFilters?.values && campaignFilters?.values.length > 0) {
      // eslint-disable-next-line no-unused-expressions,array-callback-return
      campaignFilters?.values.map((value) => {
        items.push({
          id: value.value,
          name: value.displayName,
          selected: false,
          mordorValue: value.value,
          customFilterId: value.value,
          isAutoApplied: false,
        })
      })
    }
    return {
      id: originalFacetId,
      title: campaignFilters.name,
      mordorId: campaignFilters.key,
      customFacetId: campaignFilters.key,
      hasMoreItem: false,
      isIndexableWithBrandFilter: false,
      enabled: Boolean(campaignFilters?.hasProduct || campaignFilters?.selectedValues?.length),
      items: items,
    }
  }
  return null;
}

const getReceiverTownCodeParam = ({queryParams, receiverTownCode}) => {
  const filterParams = queryParams?.[FILTER_QUERY_PARAM_NAMES.GENERIC_FILTERS] || '';

  if (filterParams?.includes(DELIVERY.NEXT_DAY_DELIVERY) && receiverTownCode) {
    return {receiverTownCode};
  }

  return {};
}

export {
  getReceiverTownCodeParam,
  generateSelectedFiltersText,
  generateInitialSelectedFiltersByFacetMap,
  generateCustomPriceRangeFilterName,
  generateCustomPriceRangeFilterValue,
  generateCustomPriceRangeFilterNameFromValuePartials,
  generateFilterApiQueryParams,
  generateFilterApiQueryString,
  generateFilterTitle,
  generateFilterAnchorTitle,
  generateFilterAnchorHref,
  generateListingFilterAnchorHref,
  generateTagFilterAnchorHref,
  generateQueryParamsFromFilter,
  updateGenericFilterParamsArray,
  extractRawPriceValuesFromRawPriceFilterValue,
  preprocessRawAppliedPriceFilter,
  clearExistingSelectedFiltersByFacetMap,
  checkForAppliedNonBrandFiltersInGetFiltersResponse,
  generateAppliedFiltersArrayForDavinci,
  generateAppliedFacetsStringForWebtrekk,
  generateFilterApiQueryParamsForSearchPageClearFacetWithoutBrand,
  getAppliedUniqueBrandFilter,
  getAppliedUniqueBrandIndexableFilterUrl,
  generateDeliveryResponseFacetMapper,
  generateCampaignFilterResponseFacetMapper,
  getAppliedCampaignFilter,
};
